import React, { useEffect, useState } from 'react';
import { GlassCard } from '../../components/GlassCard';
import { PageHeader } from '../../components/AppLayout';
import { TrendCharts } from '../../components/TrendCharts';
import { WeeklyDigest, SymptomClusters } from '../../components/AnalyticsCards';
import { useUserStore } from '../../store/userStore';
import { getInsights, generateInsight } from '../../lib/api';

const SEV = {
  info: { c: '#3B82F6', icon: 'fa-circle-info' },
  warning: { c: '#F59E0B', icon: 'fa-triangle-exclamation' },
  alert: { c: '#EF4444', icon: 'fa-bell' },
};

export default function Insights() {
  const user = useUserStore((s) => s.user);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const data = await getInsights(user.id);
      setInsights(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) load();
  }, [user?.id]); // eslint-disable-line

  const generate = async () => {
    setGenerating(true);
    try {
      await generateInsight(user.id);
      await load();
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 pt-6 sm:pt-10">
      <PageHeader
        title="AI insights"
        subtitle="Personalised patterns from your tracked data."
        right={
          <button
            data-testid="generate-insight-btn"
            onClick={generate}
            disabled={generating}
            className="aura-btn-gradient text-white text-sm font-semibold rounded-full px-4 py-2.5 flex items-center gap-2 disabled:opacity-70"
          >
            {generating ? <i className="fa-solid fa-circle-notch fa-spin" /> : <i className="fa-solid fa-wand-magic-sparkles" />}
            Generate
          </button>
        }
      />

      <div className="mb-6">
        <WeeklyDigest userId={user?.id} />
      </div>

      <div className="mb-6">
        <TrendCharts userId={user?.id} />
      </div>

      <div className="mb-6">
        <SymptomClusters userId={user?.id} />
      </div>

      {loading ? (
        <GlassCard className="p-8 text-center text-[var(--aura-ink-soft)]"><i className="fa-solid fa-circle-notch fa-spin mr-2" /> Loading…</GlassCard>
      ) : insights.length === 0 ? (
        <GlassCard className="p-10 text-center">
          <div className="grid place-items-center w-16 h-16 rounded-2xl mx-auto mb-4 bg-[var(--aura-purple)]/10 text-[var(--aura-purple)]">
            <i className="fa-solid fa-wand-magic-sparkles text-2xl" />
          </div>
          <h3 className="font-display text-xl mb-2">No insights yet</h3>
          <p className="text-[var(--aura-ink-soft)] mb-5">Generate your first AI insight based on your check-ins and assessments.</p>
          <button onClick={generate} disabled={generating} className="aura-btn-gradient text-white font-semibold rounded-full px-6 py-3 inline-flex items-center gap-2 disabled:opacity-70">
            {generating ? <i className="fa-solid fa-circle-notch fa-spin" /> : <i className="fa-solid fa-sparkles" />} Generate insight
          </button>
        </GlassCard>
      ) : (
        <div className="flex flex-col gap-4">
          {insights.map((ins, i) => {
            const s = SEV[ins.severity] || SEV.info;
            return (
              <GlassCard key={ins.id || i} className="p-6 animate-fade-up" data-testid={`insight-${i}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="grid place-items-center w-10 h-10 rounded-xl shrink-0" style={{ background: `${s.c}18`, color: s.c }}>
                    <i className={`fa-solid ${s.icon}`} />
                  </div>
                  <div>
                    <h3 className="font-display text-lg leading-tight">{ins.title}</h3>
                    <div className="text-xs text-[var(--aura-ink-soft)] capitalize">{ins.insight_type} insight</div>
                  </div>
                </div>
                <p className="text-[var(--aura-ink)] leading-relaxed">{ins.content}</p>
                {ins.action_items?.length > 0 && (
                  <ul className="mt-4 space-y-2">
                    {ins.action_items.map((a, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm">
                        <i className="fa-solid fa-circle-check mt-0.5" style={{ color: '#14B8A6' }} /> {a}
                      </li>
                    ))}
                  </ul>
                )}
              </GlassCard>
            );
          })}
        </div>
      )}
    </div>
  );
}
